import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { socialMedia } from '@config';
import { Side } from '@components';
import { Icon } from '@components/icons';

const Social = ({ isHome }) => {
  const [theme, setTheme] = useState('default');

  const colorThemes = [
    { name: 'default', color: '#64ffda', label: 'Default Cyan' },
    { name: 'yellow', color: '#D946EF', label: 'Fuchsia' },
    { name: 'purple', color: '#6366F1', label: 'Royal Blue' },
    { name: 'green', color: '#22C55E', label: 'Neon Green' },
    { name: 'red', color: '#FB923C', label: 'Sunset Orange' },
  ];

  useEffect(() => {
    setTheme('default');
    localStorage.removeItem('portfolio-theme');
  }, []);

  const applyTheme = (themeName) => {
    const root = document.documentElement;

    const themes = {
      default: { r: 100, g: 255, b: 218, hex: '#64ffda', pink: '#f57dff', blue: '#57cbff', filter: 'brightness(0) saturate(100%) invert(64%) sepia(98%) saturate(2618%) hue-rotate(158deg) brightness(101%) contrast(102%)' },
      yellow:  { r: 217, g: 70,  b: 239, hex: '#D946EF', pink: '#E879F9', blue: '#C026D3', filter: 'brightness(0) saturate(100%) invert(47%) sepia(69%) saturate(4467%) hue-rotate(251deg) brightness(101%) contrast(96%)' },
      purple:  { r: 99,  g: 102, b: 241, hex: '#6366F1', pink: '#818CF8', blue: '#4F46E5', filter: 'brightness(0) saturate(100%) invert(47%) sepia(69%) saturate(4467%) hue-rotate(225deg) brightness(101%) contrast(96%)' },
      green:   { r: 34,  g: 197, b: 94,  hex: '#22C55E', pink: '#4ADE80', blue: '#16A34A', filter: 'brightness(0) saturate(100%) invert(64%) sepia(98%) saturate(1204%) hue-rotate(111deg) brightness(96%) contrast(86%)' },
      red:     { r: 251, g: 146, b: 60,  hex: '#FB923C', pink: '#FDBA74', blue: '#EA580C', filter: 'brightness(0) saturate(100%) invert(64%) sepia(88%) saturate(1945%) hue-rotate(6deg) brightness(101%) contrast(96%)' },
    };

    const t = themes[themeName] || themes.default;
    const rgba = (a) => `rgba(${t.r}, ${t.g}, ${t.b}, ${a})`;

    root.style.setProperty('--green', t.hex);
    root.style.setProperty('--green-tint', rgba(0.1));
    root.style.setProperty('--green-tint-02', rgba(0.02));
    root.style.setProperty('--green-tint-03', rgba(0.03));
    root.style.setProperty('--green-tint-04', rgba(0.04));
    root.style.setProperty('--green-tint-05', rgba(0.05));
    root.style.setProperty('--green-tint-06', rgba(0.06));
    root.style.setProperty('--green-tint-08', rgba(0.08));
    root.style.setProperty('--green-tint-12', rgba(0.12));
    root.style.setProperty('--green-tint-15', rgba(0.15));
    root.style.setProperty('--pink', t.pink);
    root.style.setProperty('--blue', t.blue);
    root.style.setProperty('--accent-shadow', rgba(0.4));
    root.style.setProperty('--accent-shadow-30', rgba(0.3));
    root.style.setProperty('--accent-glow', rgba(0.2));
    root.style.setProperty('--current-theme-filter', t.filter);
  };

  const handleThemeChange = (themeName) => {
    setTheme(themeName);
    applyTheme(themeName);
  };

  return (
    <Side isHome={isHome} orientation="right">
      {/* social-container and its ::after line defined in global.css */}
      <div className="social-container flex flex-col items-center">

        {/* theme-toggle uses CSS parent-hover to show palette-popup */}
        <div className="theme-toggle relative mb-[7px]">
          <button
            className="bg-transparent border-0 p-[10px] cursor-pointer text-light-slate transition-all flex items-center justify-center hover:text-accent hover:-translate-y-[3px] focus:text-accent focus:-translate-y-[3px]"
            aria-label="Theme color options"
            title="Hover to see color options">
            {/* Bug fix: filter now reads var(--current-theme-filter) which applyTheme() updates */}
            <Icon name="Theme" />
          </button>

          {/* palette-popup visibility toggled by CSS: .theme-toggle:hover .palette-popup */}
          <div className="palette-popup">
            {colorThemes.map((colorTheme) => (
              <button
                key={colorTheme.name}
                style={{ backgroundColor: colorTheme.color }}
                onClick={() => handleThemeChange(colorTheme.name)}
                className={`w-5 h-5 rounded-full my-1 cursor-pointer transition-all hover:scale-110 focus:outline-none ${
                  theme === colorTheme.name
                    ? 'border-2 border-white'
                    : 'border-2 border-transparent hover:border-white'
                }`}
                aria-label={`Change theme to ${colorTheme.label}`}
                title={colorTheme.label}
              />
            ))}
          </div>
        </div>

        {/* social-list and its hover styles defined in global.css */}
        <ul className="social-list">
          {socialMedia &&
            socialMedia.map(({ url, name }, i) => (
              <li key={i}>
                <a href={url} aria-label={name} target="_blank" rel="noreferrer">
                  <Icon name={name} />
                </a>
              </li>
            ))}
        </ul>
      </div>
    </Side>
  );
};

Social.propTypes = {
  isHome: PropTypes.bool,
};

export default Social;
